import React, { useEffect, useMemo, useState } from 'react';
import { connectorsApi } from '../services/connectorsApi';
import { X, Search, Play } from 'lucide-react';

const sqlTypes = ['oracle', 'postgres', 'mysql'];

const defaultPayloadForType = (type) => {
  if (sqlTypes.includes(type)) {
    return JSON.stringify(
      {
        query: 'SELECT 1',
        fetch_type: 'all',
      },
      null,
      2
    );
  }
  return '{}';
};

const ConnectorTestModal = ({ configuration, onClose }) => {
  const [testing, setTesting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState('{}');

  const isSqlConnector = useMemo(
    () => (configuration ? sqlTypes.includes(configuration.type) : false),
    [configuration]
  );

  useEffect(() => {
    if (!configuration) {
      return;
    }
    setResult(null);
    setError(null);
    setPayload(defaultPayloadForType(configuration.type));
  }, [configuration]);

  if (!configuration) {
    return null;
  }

  const handleTest = async () => {
    try {
      setTesting(true);
      setError(null);
      const response = await connectorsApi.testConfigured(configuration.id);
      setResult({ type: 'test', status: response.data.status, data: response.data });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors du test de connexion.');
    } finally {
      setTesting(false);
    }
  };

  const handleExecute = async () => {
    let parsedPayload = {};
    if (payload && payload.trim().length > 0) {
      try {
        parsedPayload = JSON.parse(payload);
      } catch (err) {
        setError(err?.message || 'Format JSON invalide pour le payload.');
        return;
      }
    }

    try {
      setExecuting(true);
      setError(null);
      const response = await connectorsApi.executeConfigured(configuration.id, parsedPayload);
      setResult({ type: 'execute', status: response.data.status, data: response.data });
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'exécution.");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            Tester le connecteur configuré
          </h2>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 p-1"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">{configuration.name}</p>
            <p className="mt-1">Type&nbsp;: {configuration.type}</p>
            <p>App ID&nbsp;: {configuration.app_id}</p>
            <p>Environnement&nbsp;: {configuration.environment}</p>
            <p>Chemin Vault&nbsp;: {configuration.vault_path}</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleTest}
              disabled={testing || executing}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-[#E20613] text-white hover:bg-[#c90511] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Search className="h-4 w-4" />
              <span>{testing ? 'Test en cours…' : 'Tester la connexion'}</span>
            </button>

            <button
              onClick={handleExecute}
              disabled={executing || testing}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Play className="h-4 w-4" />
              <span>{executing ? 'Exécution…' : 'Lancer une requête'}</span>
            </button>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Payload (JSON)</label>
            <textarea
              value={payload}
              onChange={(event) => setPayload(event.target.value)}
              className="w-full px-3 py-2 border border-slate-300 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#E20613] min-h-[140px]"
            />
            {isSqlConnector && (
              <p className="text-xs text-slate-500">
                Par défaut, la requête SQL <code>SELECT 1</code> est utilisée. Ajustez le JSON si besoin.
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 text-sm">
              ❌ {error}
            </div>
          )}

          {result && (
            <div
              className={`border px-4 py-3 ${
                result.status === 'success'
                  ? 'bg-green-50 border-green-300 text-green-800'
                  : 'bg-red-50 border-red-300 text-red-700'
              }`}
            >
              <h4 className="font-semibold mb-3">
                {result.status === 'success' ? '✅ Succès' : '❌ Erreur'}
              </h4>

              {result.type === 'test' && result.data.pool_stats && (
                <div>
                  <h5 className="font-medium text-sm mb-2">Statistiques du pool</h5>
                  <ul className="text-sm space-y-1 pl-4">
                    <li>Pool Key&nbsp;: {result.data.pool_stats.pool_key}</li>
                    <li>Taille&nbsp;: {result.data.pool_stats.size}</li>
                    <li>En cours&nbsp;: {result.data.pool_stats.checked_out}</li>
                    <li>Disponibles&nbsp;: {result.data.pool_stats.checked_in}</li>
                    <li>Status&nbsp;: {result.data.pool_stats.status}</li>
                  </ul>
                </div>
              )}

              {result.type === 'execute' && result.data.data && (
                <div>
                  <h5 className="font-medium text-sm mb-2">Résultats</h5>
                  <pre className="bg-slate-100 border border-slate-200 px-3 py-2 overflow-x-auto text-xs">
                    {JSON.stringify(result.data.data, null, 2)}
                  </pre>
                </div>
              )}

              {result.data.error && (
                <div className="text-sm">
                  <p>{result.data.error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectorTestModal;
